import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_STATUT_OPTIONS,
  AGENCE_BIEN_TYPE_OPTIONS,
  type AgenceBien,
  type AgenceBienInput,
  type AgenceBienStatut,
  type AgenceBienType,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";

interface BienFormProps {
  open: boolean;
  onClose: () => void;
  defaultProprietaireId?: string;
  defaultImmeubleId?: string;
  initial?: AgenceBien;
}

interface FormState {
  proprietaireId: string;
  immeubleId: string;
  type: AgenceBienType | "";
  reference: string;
  etage: string;
  numero: string;
  surface: string;
  loyerMensuel: string;
  statut: AgenceBienStatut;
  description: string;
  infosComplementaires: string;
}

const EMPTY_STATE: FormState = {
  proprietaireId: "",
  immeubleId: "",
  type: "",
  reference: "",
  etage: "",
  numero: "",
  surface: "",
  loyerMensuel: "",
  statut: "disponible",
  description: "",
  infosComplementaires: "",
};

export default function BienForm({ open, onClose, defaultProprietaireId, defaultImmeubleId, initial }: BienFormProps) {
  const { immeubles, addBien, getImmeuble, updateBien } = useAgenceData();
  const { proprietaires } = useProprietaires();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        proprietaireId: initial.proprietaireId,
        immeubleId: initial.immeubleId,
        type: initial.type,
        reference: initial.reference,
        etage: initial.etage,
        numero: initial.numero,
        surface: initial.surface ? String(initial.surface) : "",
        loyerMensuel: String(initial.loyerMensuel),
        statut: initial.statut,
        description: initial.description,
        infosComplementaires: initial.infosComplementaires,
      });
      setErrors({});
      return;
    }
    setForm((prev) => {
      const next = { ...prev };
      if (defaultImmeubleId) {
        next.immeubleId = defaultImmeubleId;
        const immeuble = immeubles.find((item) => item.id === defaultImmeubleId);
        if (immeuble) next.proprietaireId = immeuble.proprietaireId;
      }
      if (defaultProprietaireId) next.proprietaireId = defaultProprietaireId;
      return next;
    });
  }, [open, initial, defaultImmeubleId, defaultProprietaireId, immeubles]);

  const proprietaireOptions = useMemo(
    () => proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () =>
      immeubles
        .filter((immeuble) => !form.proprietaireId || immeuble.proprietaireId === form.proprietaireId)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles, form.proprietaireId]
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleProprietaire = (value: string) => {
    setForm((prev) => ({ ...prev, proprietaireId: value, immeubleId: "" }));
  };

  const handleImmeuble = (value: string) => {
    const immeuble = getImmeuble(value);
    setForm((prev) => ({
      ...prev,
      immeubleId: value,
      proprietaireId: immeuble ? immeuble.proprietaireId : prev.proprietaireId,
    }));
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.proprietaireId) nextErrors.proprietaireId = "Sélectionnez le propriétaire.";
    if (!form.immeubleId) nextErrors.immeubleId = "Sélectionnez l'immeuble du bien.";
    if (!form.type) nextErrors.type = "Sélectionnez un type de bien.";
    if (!form.loyerMensuel.trim() || Number(form.loyerMensuel) <= 0) {
      nextErrors.loyerMensuel = "Indiquez un loyer mensuel valide.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: AgenceBienInput = {
      proprietaireId: form.proprietaireId,
      immeubleId: form.immeubleId,
      type: form.type as AgenceBienType,
      reference: form.reference.trim(),
      etage: form.etage.trim(),
      numero: form.numero.trim(),
      surface: Math.max(0, Number(form.surface) || 0),
      loyerMensuel: Math.max(0, Number(form.loyerMensuel) || 0),
      statut: form.statut,
      description: form.description.trim(),
      infosComplementaires: form.infosComplementaires.trim(),
    };

    if (initial) {
      updateBien(initial.id, input);
    } else {
      addBien(input);
    }
    resetAndClose();
  };

  const noProprietaire = proprietaires.length === 0;
  const noImmeuble = immeubles.length === 0;

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier le bien" : "Ajouter un bien"}
      description="Le bien est rattaché à un immeuble, donc automatiquement au propriétaire de cet immeuble."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="agence-bien-form"
            disabled={noProprietaire || noImmeuble}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le bien
          </button>
        </>
      }
    >
      <form id="agence-bien-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field
            label="Propriétaire"
            htmlFor="agence-bien-proprietaire"
            required
            error={errors.proprietaireId}
            hint="Détermine la liste des immeubles proposés."
          >
            <Select
              id="agence-bien-proprietaire"
              value={form.proprietaireId}
              onChange={(event) => handleProprietaire(event.target.value)}
              options={proprietaireOptions}
              placeholder={proprietaireOptions.length > 0 ? "Sélectionnez un propriétaire" : "Aucun propriétaire"}
              disabled={noProprietaire}
              invalid={Boolean(errors.proprietaireId)}
            />
          </Field>

          <Field
            label="Immeuble"
            htmlFor="agence-bien-immeuble"
            required
            error={errors.immeubleId}
            hint="Le propriétaire se met à jour automatiquement selon l'immeuble."
          >
            <Select
              id="agence-bien-immeuble"
              value={form.immeubleId}
              onChange={(event) => handleImmeuble(event.target.value)}
              options={immeubleOptions}
              placeholder={
                immeubleOptions.length > 0 ? "Sélectionnez un immeuble" : "Aucun immeuble disponible"
              }
              disabled={immeubleOptions.length === 0}
              invalid={Boolean(errors.immeubleId)}
            />
          </Field>

          <Field label="Type" htmlFor="agence-bien-type" required error={errors.type}>
            <Select
              id="agence-bien-type"
              value={form.type}
              onChange={(event) => update("type", event.target.value)}
              options={AGENCE_BIEN_TYPE_OPTIONS}
              placeholder="Sélectionnez un type"
              invalid={Boolean(errors.type)}
            />
          </Field>

          <Field label="Référence" htmlFor="agence-bien-reference" hint="Ex. APT-101, BQ-02, BUR-3A">
            <Input
              id="agence-bien-reference"
              value={form.reference}
              onChange={(event) => update("reference", event.target.value)}
              placeholder="Référence interne du bien"
              maxLength={60}
            />
          </Field>

          <Field label="Étage" htmlFor="agence-bien-etage" hint="Ex. Rez-de-chaussée, 1er, 2e">
            <Input
              id="agence-bien-etage"
              value={form.etage}
              onChange={(event) => update("etage", event.target.value)}
              placeholder="Étage du bien"
              maxLength={40}
            />
          </Field>

          <Field label="Numéro du bien" htmlFor="agence-bien-numero">
            <Input
              id="agence-bien-numero"
              value={form.numero}
              onChange={(event) => update("numero", event.target.value)}
              placeholder="Ex. 12, A3, 04"
              maxLength={40}
            />
          </Field>

          <Field label="Surface" htmlFor="agence-bien-surface" hint="Surface approximative en m².">
            <Input
              id="agence-bien-surface"
              type="number"
              min={0}
              max={100000}
              value={form.surface}
              onChange={(event) => update("surface", event.target.value)}
              placeholder="Ex. 75"
            />
          </Field>

          <Field label="Loyer mensuel (FCFA)" htmlFor="agence-bien-loyer" required error={errors.loyerMensuel}>
            <Input
              id="agence-bien-loyer"
              type="number"
              min={0}
              max={100000000}
              value={form.loyerMensuel}
              onChange={(event) => update("loyerMensuel", event.target.value)}
              placeholder="Ex. 75000"
              invalid={Boolean(errors.loyerMensuel)}
            />
          </Field>

          <Field label="Statut" htmlFor="agence-bien-statut">
            <Select
              id="agence-bien-statut"
              value={form.statut}
              onChange={(event) => update("statut", event.target.value)}
              options={AGENCE_BIEN_STATUT_OPTIONS}
            />
          </Field>

          <Field label="Description" htmlFor="agence-bien-description" className="md:col-span-2">
            <Textarea
              id="agence-bien-description"
              value={form.description}
              onChange={(event) => update("description", event.target.value)}
              placeholder="Décrivez brièvement le bien : nombre de pièces, équipements, atouts..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>

          <Field label="Informations complémentaires" htmlFor="agence-bien-infos" className="md:col-span-2">
            <Textarea
              id="agence-bien-infos"
              value={form.infosComplementaires}
              onChange={(event) => update("infosComplementaires", event.target.value)}
              placeholder="Ex. climatisation, compteur individuel, parking, eau chaude..."
              maxLength={600}
            />
          </Field>
        </div>

        {noProprietaire && (
          <p className="flex flex-wrap items-center gap-2 rounded-md bg-background-100 px-3.5 py-3 text-xs text-foreground-600">
            <i className="ri-information-line text-base text-foreground-400" aria-hidden="true" />
            Un bien nécessite un immeuble, lui-même rattaché à un propriétaire.
            <Link to="/agence/proprietaires" className="font-label font-medium text-primary-700 cursor-pointer">
              Créer un propriétaire
            </Link>
          </p>
        )}

        {!noProprietaire && noImmeuble && (
          <p className="flex flex-wrap items-center gap-2 rounded-md bg-background-100 px-3.5 py-3 text-xs text-foreground-600">
            <i className="ri-information-line text-base text-foreground-400" aria-hidden="true" />
            Aucun immeuble enregistré. Créez d'abord un immeuble.
            <Link to="/agence/immeubles" className="font-label font-medium text-primary-700 cursor-pointer">
              Aller aux immeubles
            </Link>
          </p>
        )}
      </form>
    </Modal>
  );
}